/**
 * SalienceOverlay — Counter-Salience visualization for the editor.
 *
 * The field_map detects "tensions" (open questions) and "dominated
 * sections" (one author crowding out others). Linear reading tends
 * to bury both — last-paragraph-wins for tensions, longest-voice-wins
 * for dominance. This overlay surfaces them so the field stays legible
 * instead of collapsing into echo.
 *
 * **Skeleton scope (Phase 2):** a fixed corner badge with counts and
 * a click-to-expand list. Per-paragraph in-line cues (glow on the
 * margin stripe, dimming for dominant authors) land in Phase 2.5 once
 * the badge data path is validated against live posts.
 *
 * The overlay sits beside any layouter (Doc / Chat / Canvas / Review)
 * since salience is a data-orthogonal lens, not a layout — see the
 * architecture rationale in Post #777 op-log seq 2181.
 */

import { useState } from 'react'
import {
  useFieldMap,
  selectTensions,
  selectDominatedSections,
} from '../hooks/useFieldMap'

interface Props {
  postId: number | null
  /** Disable the overlay entirely (e.g. in read-only preview). */
  enabled?: boolean
}

export function SalienceOverlay({ postId, enabled = true }: Props) {
  const [expanded, setExpanded] = useState(false)
  const { data, isLoading, error } = useFieldMap({ postId, enabled })

  if (!enabled || postId == null) return null

  const tensions = selectTensions(data)
  const dominated = selectDominatedSections(data)
  const hasSignal = tensions.length > 0 || dominated.length > 0

  if (isLoading && !data) return null
  if (error) return null
  if (!hasSignal && !expanded) return null

  return (
    <div className="salience-overlay" data-expanded={expanded}>
      <button
        className="salience-overlay__badge"
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-label={`Counter-Salience: ${tensions.length} offene Fragen, ${dominated.length} dominierte Sektionen`}
      >
        <span className="salience-overlay__count">{tensions.length}</span>
        <span className="salience-overlay__sep">/</span>
        <span className="salience-overlay__count salience-overlay__count--dominance">
          {dominated.length}
        </span>
      </button>
      {expanded && (
        <div className="salience-overlay__panel">
          <div className="salience-overlay__section">
            <h4>Offene Fragen ({tensions.length})</h4>
            {tensions.length === 0 ? (
              <p className="salience-overlay__empty">Keine erkannten.</p>
            ) : (
              <ul>
                {tensions.slice(0, 10).map((t, i) => (
                  <li key={`${t.block_index}-${i}`}>
                    <span className="salience-overlay__author">
                      {t.author_id.replace(/^(agent|user):/, '')}
                    </span>
                    <span className="salience-overlay__text">
                      {t.text.length > 120 ? t.text.slice(0, 117) + '…' : t.text}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="salience-overlay__section">
            <h4>Dominierte Sektionen ({dominated.length})</h4>
            {dominated.length === 0 ? (
              <p className="salience-overlay__empty">Keine erkannten.</p>
            ) : (
              <ul>
                {dominated.slice(0, 5).map(({ section, dominant_author_id }) => (
                  <li key={section.slug}>
                    <span className="salience-overlay__author">
                      {dominant_author_id.replace(/^(agent|user):/, '')}
                    </span>
                    <span className="salience-overlay__text">{section.title}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
